import { isString, isFunction, isObjectLike, isNumber } from 'lodash';
import { decode } from 'base-64';
import { ConfigsService, WindowProxy, LocaleService } from '@sm360/widget-commons-core-services';
import Logger from '@sm360/logger-sentry';
import CONSTANT from 'commons-general/constants/constant.js';

/**
 * Regroup some utility functions
 */
class UtilsService {
    /**
     * Return value to the closest rounded step if the value is between min and max.
     * Typically used in trade-in slider.
     *
     * @memberof UtilsService
     * @param {Number} value - The value to round
     * @param {Number} [step=1] - The step where we want to increment ou decrement value
     * @param {Number} [min] - Minimum inclusive value where we want to stop
     * @param {Number} [max] - Maximum inclusive value where we want to stop
     * @return {Number} The value rounded to the step
     *
     * @example
     * UtilsService.roundValueToStepIntoRange(117, 25); // 125
     * UtilsService.roundValueToStepIntoRange(112, 25); // 100
     * UtilsService.roundValueToStepIntoRange(112, 25, 102); // 102
     * UtilsService.roundValueToStepIntoRange(117, 25, null, 123); // 123
     */
    roundValueToStepIntoRange(value, step = 1, min, max) {
        if (!isNumber(value) || !isNumber(step) || (min && !isNumber(min)) || (max && !isNumber(max))) {
            const logger = new Logger('UtilsService', ConfigsService.getLoggerContext());
            logger.error('roundValueToStepIntoRange error: parameter must be a number.');
            return null;
        }
        const rounded = Math.round(value / step) * step;
        if (min && (rounded <= min || value === min)) {
            return min;
        } else if (max && (rounded >= max || value === max)) {
            return max;
        }
        return rounded;
    }

    /**
     * Give the component DisplayName from a component
     * @memberof UtilsService
     * @param {ReactComponent} Component - A React component
     * @return {string} The value of the Display Name
     */
    getComponentDisplayName(Component) {
        if (isFunction(Component) || isObjectLike(Component)) {
            return Component.displayName || Component.name || 'Component';
        }
        return '';
    }

    /**
     * Split by the space a string to have a Firstname and a Lastname
     * Return only a lastname if there's no space
     * @memberof UtilsService
     * @param {string} name - A name (with or without a space)
     * @return {{firstname: ?string, lastname: string}} The name object
     */
    splitName(name) {
        if (!isString(name)) {
            return null;
        }

        let firstname = null;
        let lastname = null;

        if (name.indexOf(' ') === -1) {
            lastname = name;
        } else {
            const splitName = name.split(' ');
            firstname = splitName.shift();
            lastname = splitName.join(' ');
        }

        return { firstname, lastname };
    }

    /**
     * Decode and parse a string to have the token information
     * @memberof UtilsService
     * @param {string} token - The encode string token
     * @return {Object} The token object
     */
    extractTokenPayload(token) {
        if (
            !isString(token) ||
            token.indexOf('.') === token.lastIndexOf('.') ||
            token.indexOf('.') === -1 ||
            token.lastIndexOf('.') === -1 ||
            !WindowProxy.get()
        ) {
            return null;
        }

        const tokenPayloadString = token.substring(token.indexOf('.') + 1, token.lastIndexOf('.'));

        return JSON.parse(this.decodeBase64(tokenPayloadString));
    }

    /**
     * Decode a string encoded in Base64
     * Wraps the {@link base64.decode} function for retro-compatibility
     * @memberof UtilsService
     * @param {string} encodedString - The base64 encoded string
     * @return {string} The decoded string or an empty string when {@link window} is not available
     */
    decodeBase64(encodedString) {
        return decode(encodedString);
    }

    /**
     * Returns the integer value of the argument, null otherwise
     * @memberof UtilsService
     * @param {?any} value the argument
     * @return {?number} the argument as an integer or null
     */
    getIntegerOrNull(value) {
        return parseInt(value, 10) || null;
    }

    // Returns the label of completion state of an `evaluationAnswers` object
    // @param {status} status of the form (ALL_COMPLETED, NOT_COMPLETED, NOT_STARTED)
    getOfficialEvaluationLabel(status, isViewOnly = false) {
        const { ALL_COMPLETED, NOT_COMPLETED, NOT_STARTED } = CONSTANT.OFFICIAL_FORM_STATUS;
        const transKeyBase = 'TRANSACTION_PROGRESSION.TRANSACTION_ACTION.TRADE_IN.COMPLETE_OFFICIAL';
        let label = '';

        if (isViewOnly) {
            label = LocaleService.t(`${transKeyBase}.VIEW_ACTION`);
        } else {
            switch (status) {
                case NOT_STARTED:
                    label = LocaleService.t(`${transKeyBase}.TAKE_ACTION`);
                    break;
                case NOT_COMPLETED:
                    label = LocaleService.t(`${transKeyBase}.RESUME_ACTION`);
                    break;
                case ALL_COMPLETED:
                    label = LocaleService.t(`${transKeyBase}.MODIFY_ACTION`);
                    break;
                default:
                    label = LocaleService.t(`${transKeyBase}.TAKE_ACTION`);
                    break;
            }
        }

        return label;
    }

    /**
     * Creates a min height for all the children elements
     * of an HTML node specified by the class names
     * @param { array } elements - list of parent HTML Elements
     * @param { array } classNames - list of class names to lookup from the parent
     */
    alignCardItems(elements, classNames) {
        const getHeights = (items) => {
            const heights = [];
            items.forEach((item) => {
                classNames.forEach((className, index) => {
                    const subitem = item.querySelector(className);
                    if (subitem)
                        heights[index] =
                            subitem.offsetHeight > (heights[index] || 0) ? subitem.offsetHeight : heights[index] || 0;
                });
            });
            return heights;
        };

        const setElementHeights = (items, heights) => {
            items.forEach((item) => {
                // eslint-disable-next-line consistent-return
                classNames.forEach((className, index) => {
                    const subitem = item.querySelector(className);
                    if (!subitem) return false;
                    subitem.style.minHeight = `${heights[index]}px`;
                });
            });
        };

        const heights = getHeights(elements);

        setElementHeights(elements, heights);
    }

    /**
     * Returns a formatted model name string, conditionally including the make.
     * If the make is usually omitted, but the model is special, it includes the make.
     *
     * @param {Object} params
     * @param {string} params.make - The car make (e.g., "Toyota").
     * @param {string} params.model - The car model (e.g., "Corolla").
     * @param {string} params.noMakeListStr - Comma-separated list of makes where make is usually hidden.
     * @param {string} params.modelListStr - Comma-separated list of model keywords that should always include make.
     * @returns {string} - A string representing the formatted model name.
     */
    getNewModelName({ make, model, noMakeListStr, modelListStr }) {
        if (noMakeListStr === '' || noMakeListStr === null || modelListStr === '' || modelListStr === null)
            return `${make} ${model}`;

        const noMakeList = noMakeListStr
            .split(',')
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);
        const modelList = modelListStr
            .split(',')
            .map((s) => s.trim().toLowerCase())
            .filter(Boolean);
        const makeLC = make.toLowerCase();
        const modelLC = model.toLowerCase();

        if (modelList.some((keyword) => modelLC.includes(keyword))) {
            return `${make} ${model}`;
        }

        // 2) else if make is in the "hide" list ⇒ hide make
        if (noMakeList.includes(makeLC)) {
            return model;
        }

        // 3) otherwise ⇒ show make
        return `${make} ${model}`;
    }
}

export default new UtilsService();
